/**
 * The coach's offline answers: a question that names a glossary term or a measure on screen is answered from the glossary or the
 * attribution, deterministically and with no model. Anything else goes to the provider. Pure; copy comes from the content pack.
 */
import type { DecisionCatalogEntry } from "@bsim/contracts";
import { DECISION_KPI_EFFECTS, decisionOfPath, type GlossaryTerm, type KpiId } from "@bsim/domain";
import { LOWER_IS_BETTER, kpiLabel, kpiNames } from "../format/labels";

const WHY_MOVED = /\bwhy\b.*\b(?:mov|chang|fell|fall|drop|rose|rise|went|slip|jump)/i;
/** "Why am I 1st / ranked / ahead / did my price fall": a question about the result, answered from the released report. */
const WHY_RESULT = /\bwhy\b[^?]*\b(?:\d+(?:st|nd|rd|th)|first|rank(?:ed)?|ahead|behind|price|score|tier|wors(?:e|t)|twin(?:'s)?|los(?:e|t|ing)|lag(?:ged|ging)?|trail(?:ed|ing)?|below)\b/i;
/** A question asking what to do or how, not what a term is: it goes to the provider even when it names a term. */
const ADVICE = /\b(?:should|shall|recommend|advise|best|better|how (?:do|can|could|should|would)|what if|which)\b/i;

const escape = (t: string): string => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** An acronym inside a label ("CET1" in "Reported CET1"): what a player types for the term. */
const acronymsOf = (label: string): string[] => label.split(/[\s·]+/).filter((w) => /^[A-Z][A-Z0-9]+$|\d/.test(w));

/** The label read without its parenthetical ("Loan losses"), and what the parenthetical holds ("bp", "NCO rate"). */
const partsOf = (label: string): string[] => [label.replace(/\s*\([^)]*\)/g, ""), ...[...label.matchAll(/\(([^)]*)\)/g)].map((m) => m[1] ?? "")];

/** The names a term answers to: each part of its label ("Margin · NIM", "Basis point (bp)"), its acronyms, its id read as words ("marked-cet1" → "marked cet1") and its measure's on-screen name. */
/** What a player calls a term in one word: the twin is the "status-quo twin" of the glossary. */
const SHORT_NAMES: Readonly<Record<string, string>> = { "status-quo-twin": "twin" };

const namesOf = (t: GlossaryTerm): string[] => [...new Set([...(SHORT_NAMES[t.termId] === undefined ? [] : [SHORT_NAMES[t.termId] ?? ""]), ...t.label.split("·").flatMap(partsOf), ...acronymsOf(t.label), t.termId.replace(/-/g, " "), ...(t.kpi === undefined ? [] : kpiNames(t.kpi))].map((n) => n.trim().toLowerCase()).filter((n) => n !== ""))];

/** Every term the question names, in the order asked, the longest name first so "marked CET1" beats "CET1"; a name inside a longer one's span does not count again. */
function termsNamed(question: string, glossary: readonly GlossaryTerm[]): GlossaryTerm[] {
  const lower = question.toLowerCase();
  const hits = glossary.flatMap((t) => namesOf(t).flatMap((n) => [...lower.matchAll(new RegExp(`(?<![a-z0-9])${escape(n)}(?![a-z0-9])`, "g"))].map((m) => ({ t, n, at: m.index ?? 0 }))));
  const taken: boolean[] = [];
  const kept = hits.sort((x, y) => y.n.length - x.n.length).filter(({ at, n }) => {
    if (taken.slice(at, at + n.length).some(Boolean)) return false;
    for (let i = at; i < at + n.length; i++) taken[i] = true;
    return true;
  });
  return [...new Map(kept.sort((x, y) => x.at - y.at).map(({ t }) => [t.termId, t])).values()];
}

const mentions = (question: string, name: string): boolean => new RegExp(`(?<![a-z0-9])${escape(name.toLowerCase())}(?![a-z0-9])`).test(question.toLowerCase());

/** A question asking which decision, choice or setting would help a measure. */
const LEVER_HELP = /\b(?:levers?|choices?|settings?|decisions?|options?)\b.*\b(?:help|improve|raise|increase|lift|lower|reduce|boost|fix|move)/i;

/** The word a measure's decisions move it by: a loss rate is helped by falling, everything else by rising. */
const verbOf = (kpi: KpiId): string => (LOWER_IS_BETTER.has(kpi) ? "falls" : "rises");

/** The parenthesis after a move whose effect is conditional (domain `DecisionEffect`). */
const caveatNote = (caveat: string | undefined, driver: string | undefined): string =>
  caveat === "ratioOnly" ? " (raises the margin ratio by shrinking earning assets; net interest income falls)"
    : caveat === "dependsOnScenario" ? ` (the direction depends on ${driver ?? "the scenario"})` : "";

/**
 * "Liquidity stress coverage rises with: Retail deposits → Lead hard, …": the decisions that move the measure and the end of their grid that does it, from the
 * catalog's own labels (`DECISION_KPI_EFFECTS`). It lists what moves the measure; the choice, and what it costs elsewhere, stays the player's.
 */
function decisionsFor(kpi: KpiId, decisions: readonly DecisionCatalogEntry[]): string | null {
  const effects = DECISION_KPI_EFFECTS[kpi] ?? [];
  const held = effects.filter((e) => e.caveat === "heldByTreasury");
  const lines = effects.filter((e) => e.caveat !== "heldByTreasury").flatMap(({ key, move, caveat, driver }) => {
    const id = decisionOfPath(key);
    const sub = key.slice(id.length + 1);
    const entry = decisions.find((d) => d.decisionId === id);
    const options = entry?.options[sub];
    const end = move === "up" ? options?.at(-1) : options?.[0];
    return entry === undefined || end === undefined ? [] : [`${entry.label}${sub === "" ? "" : ` (${entry.subCaptions?.[sub] ?? sub})`} → ${end.label}${caveatNote(caveat, driver)}`];
  });
  const heldNote = held.length === 0 ? "" : ` ${held.map((e) => decisions.find((d) => d.decisionId === decisionOfPath(e.key))?.label).filter((l) => l !== undefined).join(" and ")} do not move it: treasury holds it at the liquidity target, so their effect shows in ${held[0]?.lands ?? "funding cost"}.`;
  return lines.length === 0 ? null : `${kpiLabel(kpi)} ${verbOf(kpi)} with: ${lines.join(", ")}. Each move trades against other measures; the choice is yours.${heldNote}`;
}

/**
 * `explain` is keyed by KPI id; `results` is the explanation of the released result (price, twin, tier), null before one exists; a null return
 * means the provider should answer. `firstYear`: no year has closed, so no measure has moved yet. `decisions` is the catalog's, for "which decision could help X".
 */
export function offlineAnswer(question: string, glossary: readonly GlossaryTerm[], explain: Readonly<Record<string, string>>, firstYear = false, results: string | null = null, decisions: readonly DecisionCatalogEntry[] = []): string | null {
  const terms = termsNamed(question, glossary);
  const term = terms[0];
  if (LEVER_HELP.test(question)) {
    const helped = terms.flatMap((t) => (t.kpi === undefined ? [] : [decisionsFor(t.kpi, decisions)])).filter((l) => l !== null);
    if (helped.length > 0) return helped.join("\n\n");
  }
  // A question that names a measure it can explain ("why did NIM move against my twin") is about that measure, not the whole result.
  const kpi = Object.keys(explain).find((k) => kpiNames(k as KpiId).some((n) => mentions(question, n)));
  if (results !== null && kpi === undefined && WHY_RESULT.test(question)) return results;
  if (WHY_MOVED.test(question)) {
    if (kpi !== undefined) return explain[kpi] ?? null;
    if (firstYear && term?.kpi !== undefined) return `${term.label} has not moved yet: your first year is still open, so there is no result to explain. The cockpit shows where it starts, and the Decide preview shows where this year's choices would take it.`;
  }
  if (term === undefined || WHY_MOVED.test(question) || ADVICE.test(question)) return null;
  // One term gets its full entry; several get one short answer each.
  return terms.map((t) => (terms.length === 1 ? `${t.label}: ${t.plain} ${t.whyItMatters}` : `${t.label}: ${t.plain}`)).join("\n\n");
}
